import React from "react";
import { z } from "zod";

import { FormDatePicker, FormField, FormRadioButtons, PanelFilters } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../../../components/Inputs/SelectEmployee";

const SurveyFilterSchema = z.object({
	status: z.string().optional(),
	startDateGte: z.date().nullish(),
	startDateLte: z.date().nullish(),
	endDateGte: z.date().nullish(),
	endDateLte: z.date().nullish(),
	createdBy: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
});

export type SurveyFilters = z.infer<typeof SurveyFilterSchema>;

type SurveyFilterPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	filters: SurveyFilters;
	onApplyFilters: (filters: SurveyFilters) => void;
};

export const SurveyFilterPanel = ({ isOpen, onClose, filters, onApplyFilters }: SurveyFilterPanelProps) => {
	const defaultValues: SurveyFilters = {
		status: filters.status ?? "all",
		startDateGte: filters?.startDateGte,
		startDateLte: filters?.startDateLte,
		endDateGte: filters?.endDateGte,
		endDateLte: filters?.endDateLte,
		createdBy: filters?.createdBy,
	};

	const handleApplyFilters = ({
		value,
	}: {
		value: Partial<SurveyFilters>;
		resetForm: () => void;
		setError: (field: keyof SurveyFilters, error: string) => void;
	}) => {
		const formattedState = { ...value };
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key as keyof SurveyFilters];
			if (
				filterValue === null ||
				filterValue === undefined ||
				(Array.isArray(filterValue) && filterValue.length === 0) ||
				filterValue === "all"
			) {
				delete formattedState[key as keyof SurveyFilters];
			}
		});
		onApplyFilters(formattedState);
		onClose();
	};

	const statusOptions = [
		{ label: "All", value: "all" },
		{ label: "Draft", value: "draft" },
		{ label: "Scheduled", value: "scheduled" },
		{ label: "Open", value: "active" },
		{ label: "Closed", value: "closed" },
	];

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={SurveyFilterSchema}
			defaultValues={defaultValues}
			onSubmit={handleApplyFilters}
		>
			<div className="space-y-4">
				<FormRadioButtons id="status" label="Status" options={statusOptions} />
				<FormDatePicker id="startDateGte" label="Start Date After" clearable={true} />
				<FormDatePicker id="startDateLte" label="Start Date Before" clearable={true} />
				<FormDatePicker id="endDateGte" label="End Date After" clearable={true} />
				<FormDatePicker id="endDateLte" label="End Date Before" clearable={true} />
				<FormField<{ id: string; name: string } | null>
					id="createdBy"
					label="Created By"
					input={(field) => (
						<SingleSelectEmployee
							placeholder="Search for an employee..."
							value={field.value ?? undefined}
							onChange={(employeeId, employeeName) => {
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								);
							}}
						/>
					)}
				/>
			</div>
		</PanelFilters>
	);
};
